import { useEffect, useMemo, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ENTITY_CHANGE_SUBSCRIPTION } from 'graphql/app/appSubscriptions';
import { showToastr } from 'store/actions/app/appActions';
import { deepEquals } from 'app/utils/utils';

const entityTypeMap = {
    system_task: 'task',
    system_process: 'process',
};

const pendingModifiedTimers = new Map(); // message -> timerId

function cancelPendingModifiedToasts(message) {
    if (message) {
        const timer = pendingModifiedTimers.get(message);
        if (timer) {
            clearTimeout(timer);
            pendingModifiedTimers.delete(message);
        }
    } else {
        pendingModifiedTimers.forEach(timer => clearTimeout(timer));
        pendingModifiedTimers.clear();
    }
}

export function scheduleModifiedToast(toastFn, message, delay = 2000) {
    cancelPendingModifiedToasts(message);
    const timer = setTimeout(() => {
        pendingModifiedTimers.delete(message);
        toastFn({ severity: 'info', detail: message });
    }, delay);
    pendingModifiedTimers.set(message, timer);
}

const EntityChangeSubscription = ({
    id,
    type,
    onEntityClosed,
    onEntityChange,
    subscriptionQuery,
    showToast,
    entityNameOverride,
    currentData,
}) => {
    const dispatch = useDispatch();
    const userId = useSelector(state => state.user.profile.id);
    const lastProcessedRef = useRef(null);

    const query = subscriptionQuery || ENTITY_CHANGE_SUBSCRIPTION;

    const { data } = useSubscription(query, {
        variables: {
            type: type,
            id: id,
        },
        skip: !id || !type,
    });

    const entityName = useMemo(() => entityNameOverride || entityTypeMap[type] || 'entity', [type, entityNameOverride]);

    useEffect(() => {
        if (data?.onEntityChange) {
            const _data = data.onEntityChange;
            if (!_data) return;
            const { primary, modifiedBy } = _data || {};
            const closedBy = modifiedBy?.id;

            const dataFingerprint = JSON.stringify(_data);
            if (lastProcessedRef.current === dataFingerprint) return;
            lastProcessedRef.current = dataFingerprint;

            if (closedBy === userId) return;

            if (currentData) {
                const skipKeys = new Set(['__typename', 'id', 'modifiedBy', 'modifiedDate']);
                const hasChange = Object.keys(_data).some(
                    key => !skipKeys.has(key) && !deepEquals(_data[key], currentData[key])
                );
                if (!hasChange) return;
            }

            if (primary?.closedDate && onEntityClosed) {
                cancelPendingModifiedToasts();
                if (showToast) {
                    scheduleModifiedToast(showToastr({ severity: 'info', detail: `This ${entityName} has been closed by another user.` }));
                }
                onEntityClosed(_data);
            } else if (onEntityChange) {
                onEntityChange(_data);
                if (showToast) {
                    cancelPendingModifiedToasts();
                    scheduleModifiedToast(
                        (toast) => dispatch(showToastr(toast)),
                        `This ${entityName} has been modified by another user.`
                    );
                }
            }
        }
    }, [data, dispatch, onEntityClosed, onEntityChange, userId, entityName, showToast, id, type, currentData]);

    return null;
};

EntityChangeSubscription.propTypes = {
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    onEntityClosed: PropTypes.func,
    onEntityChange: PropTypes.func,
    subscriptionQuery: PropTypes.object,
    showToast: PropTypes.bool,
    entityNameOverride: PropTypes.string,
    currentData: PropTypes.object,
};

EntityChangeSubscription.defaultProps = {
    onEntityClosed: null,
    onEntityChange: null,
    subscriptionQuery: null,
    showToast: true,
    currentData: null,
};

export default EntityChangeSubscription;
